/**
 * vio_node_helpers ApplicationSuccess shape:
 * `{ errorCode, errorDescription, Data: payload }`
 * Success uses errorCode `"000000"`. Auth / unhandled errors often still return HTTP 200
 * with a non-zero errorCode — callers must reject those (see assertVioApiSuccess).
 *
 * Special case: when JWT expires within the idle window the API returns
 * `{ errorCode: "999999", errorDescription: "Token Renewed", Data: { ntk } }`
 * without running the handler — CommonApiService must store `ntk` and retry.
 */
export const VIO_NO_ERROR = '000000';

export function unwrapVioResponseData(res: unknown): unknown {
  if (res != null && typeof res === 'object' && 'Data' in (res as object)) {
    return (res as Record<string, unknown>)['Data'];
  }
  return res;
}

/** New JWT from a "Token Renewed" envelope, or null. */
export function extractRenewedAccessToken(res: unknown): string | null {
  if (res == null || typeof res !== 'object' || Array.isArray(res)) return null;
  const o = res as Record<string, unknown>;
  const code = String(o['errorCode'] ?? '').trim();
  if (!code || code === VIO_NO_ERROR) return null;

  const data = unwrapVioResponseData(o);
  let ntk: unknown;
  if (data != null && typeof data === 'object' && !Array.isArray(data)) {
    const d = data as Record<string, unknown>;
    ntk = d['ntk'] ?? d['NTK'] ?? d['token'];
  }
  if (ntk == null || String(ntk).trim() === '') return null;

  const desc = String(o['errorDescription'] ?? '').toLowerCase();
  if (desc.includes('token renewed') || desc.includes('renewed')) {
    return String(ntk).trim();
  }
  // Some builds only put ntk in Data without a clear description.
  if (Object.keys(data as object).every((k) => ['ntk', 'NTK', 'token'].includes(k))) {
    return String(ntk).trim();
  }
  return null;
}

/**
 * If the body is a vio envelope with a failed errorCode, throw so HttpClient callers hit `error:`.
 * Safe to call on plain payloads (arrays/objects without errorCode).
 */
export function assertVioApiSuccess(res: unknown): unknown {
  if (res == null || typeof res !== 'object' || Array.isArray(res)) {
    return res;
  }
  const o = res as Record<string, unknown>;
  if (!('errorCode' in o)) {
    return res;
  }
  const code = String(o['errorCode'] ?? '').trim();
  if (!code || code === VIO_NO_ERROR) {
    return res;
  }
  let desc = String(
    o['errorDescription'] ??
      (typeof o['message'] === 'string' ? o['message'] : '') ??
      'Request failed',
  ).trim();

  const lower = desc.toLowerCase();
  if (
    lower.includes('unable to find the user') ||
    lower.includes('invalid token') ||
    lower.includes('invalid autorization') ||
    lower.includes('invalid authorization') ||
    lower.includes('please relogin') ||
    lower.includes('unauthorised') ||
    lower.includes('unauthorized')
  ) {
    desc =
      'Your session is no longer valid (server restart or logout). Please log in again, then retry.';
  }

  const err = new Error(desc || 'Request failed') as Error & {
    error: Record<string, unknown>;
    status: number;
    statusText: string;
  };
  err.error = o;
  err.status = 200;
  err.statusText = 'Vio API error';
  throw err;
}

/** Unwrap Data after asserting success (use in CommonApiService map). */
export function unwrapVioSuccessData(res: unknown): unknown {
  return unwrapVioResponseData(assertVioApiSuccess(res));
}

/** Read nested payload from an error or success body (HTTP `error.error` or raw POST body). */
export function readVioDataField(body: unknown): unknown {
  if (body == null || typeof body !== 'object') return undefined;
  const o = body as Record<string, unknown>;
  return o['Data'] ?? o['data'];
}

/** Rows from listLessonReferences (array or `{ references: [...] }`). */
export function unwrapLessonReferencesList(res: unknown): unknown[] {
  const data = unwrapVioResponseData(res) ?? res;
  if (Array.isArray(data)) return data;
  if (data != null && typeof data === 'object') {
    const refs = (data as Record<string, unknown>)['references'];
    if (Array.isArray(refs)) return refs;
  }
  return [];
}

/** Lesson row from getLesson / getLessonMeta (handles wrapped or unwrapped response). */
export function unwrapLessonRow(res: unknown): Record<string, unknown> | null {
  const data = unwrapVioResponseData(res);
  if (data != null && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  if (res != null && typeof res === 'object' && !Array.isArray(res)) {
    return res as Record<string, unknown>;
  }
  return null;
}
